import { useEffect, useState, useCallback } from "react";
import {
  HeartHandshake, AlertTriangle, RefreshCw, RotateCcw, Loader2,
  PlayCircle, Clock, ShieldCheck,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import InlineConfirm from "../../components/admin/InlineConfirm";

/**
 * Brief 3.4.10 PR 3 — Admin reassurance overview page.
 *
 * Lives at /admin/ops/reassurance. Super-admin only.
 *
 * Surfaces the three buckets the evaluator + escalation produce:
 *   • applicable_now  — would fire on next tick
 *   • already_sent    — sentinel stamped, no escalation yet
 *   • escalated       — sentinel > 5d old, ops_dm to AM fired
 *
 * Admins can:
 *   • Re-arm a sentinel (clear-sentinel CTA — useful after a state
 *     correction that should reset the nudge cycle)
 *   • Fire the loop on-demand (manual-tick — same outcome as the 10min
 *     scheduled tick, honours the env kill-switch identically)
 */
const TRIGGER_LABEL = {
  submitted_and_quiet: "Submitted · quiet",
  info_requested_and_quiet: "Info requested · quiet",
  awaiting_documents_and_quiet: "Awaiting docs · quiet",
  approved_no_sandbox_txn: "Approved · no sandbox txn",
};

export default function AdminReassurance() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ticking, setTicking] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ops/reassurance/overview");
      setData(data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load overview.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (user?.role !== "super_admin") {
    return <div data-testid="reassurance-forbidden" className="text-rose-200">Super-admin only.</div>;
  }

  const onTick = async () => {
    setTicking(true);
    try {
      const { data: stats } = await api.post("/admin/ops/reassurance/tick");
      if (stats.skipped_disabled) {
        toast.warning("Loop disabled — set TELEGRAM_REASSURANCE_ENABLED=true in the worker env.");
      } else {
        toast.success(`Tick fired — enqueued ${stats.enqueued ?? 0}, escalated ${stats.escalated ?? 0}.`);
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tick failed.");
    } finally {
      setTicking(false);
    }
  };

  const onClearSentinel = async (merchantId, trigger) => {
    try {
      await api.post("/admin/ops/reassurance/clear-sentinel", {
        merchant_id: merchantId, trigger,
      });
      toast.success("Sentinel cleared.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear.");
    }
  };

  return (
    <div data-testid="reassurance-page" className="max-w-6xl space-y-6 pb-16">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <HeartHandshake className="h-4 w-4 text-cyan-300/80" />
            <h1 className="text-[18px] tracking-wide text-white">Reassurance</h1>
          </div>
          <p className="mt-2 text-[12.5px] text-zinc-400 max-w-2xl">
            Proactive check-ins to merchants stuck or quiet during onboarding.
            One nudge per trigger; silent 5-day escalation to the AM after that.
            Quiet hours 09:00–18:00 UTC honoured at send time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            data-testid="reassurance-refresh"
            className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.10] px-3 py-1.5 text-[11.5px] text-zinc-300 hover:bg-white/[0.04]"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={onTick}
            disabled={ticking}
            data-testid="reassurance-manual-tick"
            className="inline-flex items-center gap-1.5 rounded-md border border-cyan-400/30 bg-cyan-400/[0.10] px-3 py-1.5 text-[11.5px] text-cyan-100 hover:bg-cyan-400/[0.18] disabled:opacity-40"
          >
            {ticking ? <Loader2 className="h-3 w-3 animate-spin" /> : <PlayCircle className="h-3 w-3" />}
            Fire tick now
          </button>
        </div>
      </header>

      {data && (
        <section data-testid="reassurance-state-banner" className="grid grid-cols-2 gap-3">
          <StatusChip
            ok={data.loop_enabled}
            label={data.loop_enabled ? "Loop enabled" : "Loop disabled (env)"}
            icon={ShieldCheck}
            testid="reassurance-loop-state"
          />
          <StatusChip
            ok={!data.quiet_hours_now}
            label={data.quiet_hours_now ? "Quiet hours active (sends paused)" : "Within active window"}
            icon={Clock}
            testid="reassurance-quiet-hours-state"
          />
        </section>
      )}

      {error && (
        <div className="rounded-xl border border-rose-400/20 bg-rose-400/[0.03] p-4 text-rose-200 text-[12.5px]" data-testid="reassurance-error">
          {error}
        </div>
      )}

      {data && (
        <>
          <Bucket
            title="Applicable now"
            subtitle="Would fire on next tick (or right now via Fire tick)."
            rows={data.applicable_now}
            kind="applicable"
          />
          <Bucket
            title="Already sent · awaiting reply"
            subtitle="Sentinel stamped. After 5d silent, Nerea escalates to AM."
            rows={data.already_sent}
            kind="already_sent"
            onClear={onClearSentinel}
          />
          <Bucket
            title="Escalated to AM"
            subtitle="Sentinel > 5d, ops_dm fired. Per policy Nerea does not nudge again."
            rows={data.escalated}
            kind="escalated"
            onClear={onClearSentinel}
            warn
          />
        </>
      )}
    </div>
  );
}

function StatusChip({ ok, label, icon: Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border px-3 py-2 flex items-center gap-2 text-[12px] ${
        ok ? "border-emerald-400/30 bg-emerald-400/[0.05] text-emerald-200"
           : "border-amber-400/30 bg-amber-400/[0.05] text-amber-200"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
  );
}

function Bucket({ title, subtitle, rows, kind, onClear, warn }) {
  return (
    <section
      data-testid={`reassurance-bucket-${kind}`}
      className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5"
    >
      <header className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className={`text-[13.5px] tracking-wide ${warn ? "text-amber-100" : "text-white"} flex items-center gap-2`}>
            {warn && <AlertTriangle className="h-3.5 w-3.5 text-amber-300" />}
            {title}
            <span className="text-[10.5px] text-zinc-500 font-mono">· {rows.length}</span>
          </h3>
          <div className="text-[11.5px] text-zinc-500 mt-0.5">{subtitle}</div>
        </div>
      </header>
      {rows.length === 0 ? (
        <div className="text-zinc-500 text-[12px] italic">— none —</div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r, i) => (
            <li
              key={`${r.merchant_id}_${r.trigger || r.sentinel_field || i}`}
              data-testid={`reassurance-row-${r.merchant_id}-${r.trigger || ""}`}
              className="rounded-md border border-white/[0.06] bg-black/20 p-3 flex flex-wrap items-center gap-3 text-[12px]"
            >
              <a
                href={`/admin/merchants/${r.merchant_id}`}
                className="font-mono text-cyan-200 hover:underline shrink-0"
              >
                {r.legal_name || r.merchant_id}
              </a>
              <span className="rounded border border-white/[0.10] bg-white/[0.02] px-2 py-0.5 text-[10.5px] text-zinc-300 font-mono">
                {r.status}
              </span>
              {r.trigger && (
                <span className={`rounded border px-2 py-0.5 text-[10.5px] ${
                  warn ? "border-amber-400/30 bg-amber-400/[0.06] text-amber-100"
                       : "border-cyan-400/30 bg-cyan-400/[0.06] text-cyan-100"
                }`}>
                  {TRIGGER_LABEL[r.trigger] || r.trigger}
                </span>
              )}
              {r.age_hours != null && (
                <span className="text-[10.5px] text-zinc-500 font-mono">
                  age {Math.round(r.age_hours)}h
                </span>
              )}
              {r.sent_at && (
                <span className="text-[10.5px] text-zinc-500 font-mono">
                  sent {r.sent_at.slice(0, 16)}
                </span>
              )}
              {r.escalated_at && (
                <span className="text-[10.5px] text-amber-200 font-mono">
                  escalated {r.escalated_at.slice(0, 16)}
                </span>
              )}
              {onClear && r.trigger && (
                <InlineConfirm
                  message={`Clear '${TRIGGER_LABEL[r.trigger] || r.trigger}'?`}
                  onConfirm={() => onClear(r.merchant_id, r.trigger)}
                  testid={`reassurance-clear-sentinel-${r.merchant_id}`}
                  tone="danger"
                  className="ml-auto"
                  trigger={({ onClick }) => (
                    <button
                      type="button"
                      data-testid={`reassurance-clear-sentinel-${r.merchant_id}`}
                      onClick={onClick}
                      className="inline-flex items-center gap-1 rounded border border-white/[0.08] px-2 py-0.5 text-[10.5px] text-zinc-300 hover:bg-white/[0.04]"
                    >
                      <RotateCcw className="h-2.5 w-2.5" /> Clear sentinel
                    </button>
                  )}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
